import React, { useEffect } from "react";
import { Modal, Card, Row, Col, Typography, Form, DatePicker, InputNumber, Select, Input, Button, Space, message } from "antd";
import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { FORMAT_DATE, PAYMENT_METHOD } from "../../../constants/constants";
import { formatInputNumber, parseInputNumber, formatNumber } from "../../../utils/funcHelper";

const { Title, Text } = Typography;

const PaymentDebtModal = ({ isOpen, onClose, onSave, data }) => {
  const [form] = Form.useForm();

  const remaining = data?.payment_summary?.real_remaining ?? 0;
  const totalPaid = data?.payment_summary?.total_paid ?? 0;
  const finalAmount = data?.payment_summary?.total_debt_initial ?? 0;
  const remaining_system = data?.payment_summary?.remaining_system ?? 0;
  const typeDebt = data?.debt_type || "service";

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      form.setFieldsValue({
        payment_date: dayjs(),
        amount: undefined,
        payment_method: undefined,
        note: "",
      });
    }
  }, [isOpen, data]);

  const handleFinish = (values) => {
    const amount = Number(values.amount || 0);
    if (amount <= 0) {
      message.warning("Vui lòng nhập số tiền hợp lệ");
      return;
    }
    if (amount > remaining) {
      message.warning("Số tiền thanh toán không được vượt quá nợ còn lại");
      return;
    }

    const payload = {
      debt_type: typeDebt,
      ref_id: data?.ref_id,
      customer_id: data?.customer?.customer_id,
      payment_date: values.payment_date ? values.payment_date.format(FORMAT_DATE.YYYYMMDDHHMMSS) : null,
      amount,
      payment_method: values.payment_method,
      note: values.note?.trim() || "",
    };
    if (onSave) {
        onSave(payload);
        onClose();
    }
  };

  return (
    <Modal
      title="Thanh toán nợ"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width="720px"
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        <Card size="small" style={{ borderRadius: 8 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Thông tin khách hàng</Title>
          <Row gutter={16}>
            <Col span={12}><Text><strong>Khách hàng:</strong> {data?.customer?.customer_name}</Text></Col>
            <Col span={12} style={{ textAlign: "right" }}><Text><strong>SĐT:</strong> {data?.customer?.phone}</Text></Col>
          </Row>
        </Card>

        <Card size="small" style={{ borderRadius: 8 }}>
          <Title level={5} style={{ marginBottom: 8 }}>{typeDebt === 'export' ? 'Thông tin phiếu xuất' : 'Thông tin dịch vụ'}</Title>
          <Row gutter={16}>
            <Col span={8}><Text><strong>{typeDebt === 'export' ? 'Mã phiếu' : 'Dịch vụ'}:</strong> {data?.service?.name}</Text></Col>
            <Col span={8} style={{ textAlign: "center" }}><Text><strong>Ngày:</strong> {typeDebt === 'export' ? dayjs(data?.service?.export_date).format(FORMAT_DATE.DD_MM_YYYY) : dayjs(data?.service?.regis_date).format(FORMAT_DATE.DD_MM_YYYY) }</Text></Col>
            <Col span={8} style={{ textAlign: "right" }}>
              <Text><strong>Chi nhánh:</strong> {data?.service?.branch_name}</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={8}><Text><strong>Nợ ban đầu:</strong> <b>{formatNumber(remaining_system, 0)}</b> đ</Text></Col>
            <Col span={8} style={{ textAlign: "center" }}><Text><strong>Đã thanh toán:</strong> <b>{formatNumber(totalPaid, 0)}</b> đ</Text></Col>
            <Col span={8} style={{ textAlign: "right" }}><Text><strong>Nợ còn lại:</strong> <span style={{ color: "red" }}><b>{formatNumber(remaining, 0)}</b> đ</span></Text></Col>
          </Row>
        </Card>

        <Card size="small" style={{ borderRadius: 8 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Thông tin thanh toán</Title>
          <Form layout="vertical" form={form} onFinish={handleFinish}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Ngày thanh toán"
                  name="payment_date"
                  rules={[{ required: true, message: "Vui lòng chọn ngày thanh toán" }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format={FORMAT_DATE.DDMMYYYY}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Số tiền (vnđ)"
                  name="amount"
                  rules={[{ required: true, message: "Vui lòng nhập số tiền" }]}
                >
                  <InputNumber
                    min={0}
                    className="input-number-right input-number-cus-ant"
                    formatter={formatInputNumber}
                    parser={parseInputNumber}
                    style={{ width: "100%" }}
                    placeholder="Nhập số tiền"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Phương thức"
                  name="payment_method"
                  rules={[{ required: true, message: "Vui lòng chọn phương thức" }]}
                >
                  <Select
                    placeholder="Chọn phương thức"
                    options={PAYMENT_METHOD}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Nội dung" name="note" rules={
                    [
                        { max: 500, message: "Nội dung không được vượt quá 500 ký tự" },
                        { required: true, message: "Vui lòng nhập nội dung" }
                    ]
                }>
                  <Input.TextArea rows={3} placeholder="Nhập nội dung" />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="end">
              <Space>
                <Button onClick={onClose} icon={<CloseOutlined />}>Đóng</Button>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>Lưu</Button>
              </Space>
            </Row>
          </Form>
        </Card>
      </Space>
    </Modal>
  );
};

export default React.memo(PaymentDebtModal);